import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { Platform } from "react-native";
import { useFragment } from "react-relay";

import { Button } from "@whoosh/design-system/src/components/controls";
import { HStack, Text } from "@whoosh/design-system/src/components/primitive";
import { getWebApiHost } from "@whoosh/relay/src/utils/getWebApiHost";

import { MasqueradeBarPatronFragment_session$key } from "./__generated__/MasqueradeBarPatronFragment_session.graphql";
import { getPatronMasqueradeBackgroundByEnv } from "./masqueradeBarColors";

const getMasqueradeBarColor = () => {
  if (Platform.OS !== "web") {
    return "";
  } else {
    const host = window.location.host;

    if (host === "app.whoosh.io") {
      return getPatronMasqueradeBackgroundByEnv("prod");
    } else if (host === "replica.whoosh.io") {
      return getPatronMasqueradeBackgroundByEnv("replica");
    } else if (host === "staging.whoosh.io") {
      return getPatronMasqueradeBackgroundByEnv("staging");
    } else if (host === "demo.whoosh.io") {
      return getPatronMasqueradeBackgroundByEnv("demo");
    } else if (
      host.includes("localhost") ||
      host.includes("127.0.0.1") ||
      host === "dev.whoosh.io" ||
      host === "qa.whoosh.io"
    ) {
      return getPatronMasqueradeBackgroundByEnv();
    } else {
      //Review App
      return getPatronMasqueradeBackgroundByEnv("review");
    }
  }
};

export const checkIsLocal = () => {
  if (Platform.OS !== "web") {
    return false;
  } else {
    const host = window.location.host;
    return host.includes("localhost");
  }
};

type Props = {
  sessionRef: MasqueradeBarPatronFragment_session$key;
};

export const MasqueradeBar = ({ sessionRef }: Props) => {
  const session = useFragment(MasqueradeBarPatronFragmentString, sessionRef);

  const isLocal = checkIsLocal();

  const endMasquerade = async () => {
    const apiBase = await getWebApiHost({});

    const apiHost = !isLocal ? `${apiBase}` : "localhost:4000";
    const protocol =
      apiHost === "localhost:4000" || apiHost === "127.0.0.1:4000"
        ? "http"
        : "https";
    const url = `${protocol}://${apiHost}`;

    await fetch(`${url}/private/end_masquerade`, {
      method: "DELETE",
      credentials: "include",
      redirect: "manual",
    });

    const torchAdminHost = !isLocal ? `${apiBase}` : "localhost:4000";
    const backendUrl = `${protocol}://${torchAdminHost}`;
    window.location.replace(`${backendUrl}/admin`);
  };

  if (!session?.masquerader) {
    return null;
  }

  return (
    <HStack
      width="100%"
      position="relative"
      height={60}
      px={12}
      justifyContent="space-between"
      alignItems="center"
      style={{ backgroundColor: getMasqueradeBarColor(), zIndex: 10000 }}
    >
      <Text maxFontSizeMultiplier={2} color="text-inverse" fontSize={16}>
        {t`Logged in as:`}
        <Text
          maxFontSizeMultiplier={2}
          variant="mainBaseSemiBold"
          fontWeight={600}
        >{` ${session.currentClientProfile?.member?.email}`}</Text>
      </Text>
      <Button
        onPress={endMasquerade}
        variant="secondary"
        size="small"
        accessibilityLabel={t`End Masquerade button`}
      >
        {t`End Masquerade`}
      </Button>
    </HStack>
  );
};

export const MasqueradeBarPatronFragmentString = graphql`
  fragment MasqueradeBarPatronFragment_session on Session {
    currentClientProfile {
      member {
        ... on Member {
          email
        }
      }
    }
    masquerader {
      email
    }
  }
`;
